import {EventHandlerInterface} from 'app/components/tv/handler/EventHandlerInterface';
import type VideoAdsInitializedEvent from 'app/components/tv/events/VideoAdsInitializedEvent';
import type Video from 'app/components/tv/models/video';
import ChangedContentEvent from 'app/components/tv/events/ChangedContentEvent';
import VideoCreator from 'app/components/tv/models/VideoCreator';
import {captureSentryException} from 'app/services/sentry/sentryLogging';
import type {VideoJsPlayer} from 'video.js';
import VideoAdController from 'app/components/tv/VideoAdController';
import VideoAdsException from 'app/exceptions/VideoAdsException';
import AdFinishedEvent from 'app/components/tv/events/AdFinishedEvent';

export default class PlayContentHandler implements EventHandlerInterface {
  private readonly player: VideoJsPlayer;
  private readonly adController: VideoAdController;

  constructor(getPlayer: () => VideoJsPlayer, getAdController: () => VideoAdController) {
    this.player = getPlayer();
    this.adController = getAdController();
  }

  handle(event: VideoAdsInitializedEvent | ChangedContentEvent | AdFinishedEvent): void {
    const videoToPlay = this.getVideoToPlay(event);
    try {
      this.adController.requestAds();
    } catch (error) {
      if (!(error instanceof VideoAdsException)) {
        captureSentryException(error);
      }
      this.playContent(videoToPlay);
    }
  }

  private getVideoToPlay(event: VideoAdsInitializedEvent | ChangedContentEvent | AdFinishedEvent): Video {
    if (event instanceof ChangedContentEvent && event.nextVideo) {
      return event.nextVideo;
    }
    const currentItem = this.player.playlistPlugin().playlist_.getCurrentItem();
    return VideoCreator.createVideo(currentItem);
  }

  private playContent(videoToPlay: Video): void {
    this.player.src(videoToPlay.sources);
    this.player.ready(() => {
      this.setStarttime(videoToPlay);
      this.player.play()?.catch(() => {
        // FIXME: "DOMException - The play() request was interrupted" occurs silently on video src change
        // See: https://developer.chrome.com/blog/play-request-was-interrupted
        // This has no impact on functionality as long as an empty catch-block is present
        // player.pause() is commented because it has no effect
        // this.player.pause();
      });
    });
  }

  private setStarttime = (video: Video) => {
    try {
      this.player.currentTime(video.startTime);
      const currentTime = this.player.currentTime();
      const startTime = video.startTime;
      if (currentTime !== startTime) {
        this.captureFailedToSetStartTime(new Error('Failed to set starttime'), video.category, currentTime, startTime);
      }
    } catch (e: any) {
      this.captureFailedToSetStartTime(e, video.category, this.player.currentTime(), video.startTime);
    }
  };

  private captureFailedToSetStartTime(error: any, videoSource: string, currentTime: number, startTime: number): void {
    captureSentryException(error, {
      tags: {videoSource},
      extra: {currentTime, startTime},
    });
  }
}
